import { Component, OnInit, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import * as Highcharts from 'highcharts';
import { ApproveButtonRendererComponent } from './approve-button-renderer.component';
import Swal from 'sweetalert2';
import { CurrencyPipe } from '@angular/common';
import { TreeNode } from 'primeng/api';

interface FlatApprovalData {
  departamento: string;
  centroCosto: string;
  ordenCompra: string;
  descripcion: string;
  valor: number;
  nivel: number;
  isSubtotal?: boolean;
  aprobadoNivel1?: boolean;
  aprobadoNivel2?: boolean;
  aprobadoNivel3?: boolean;
  aprobadoNivel4?: boolean;
}

@Component({
  selector: 'app-approval-dashboard',
  templateUrl: './approval-dashboard.component.html',
  styleUrls: ['./approval-dashboard.component.css']
})
export class ApprovalDashboardComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  @ViewChild('pieChartContainer') pieChartContainer!: ElementRef;
  private gridApi!: GridApi;
  private chart: any;
  private pieChart: any;
  
  rowData: FlatApprovalData[] = [];
  private allFlatData: FlatApprovalData[] = [];
  treeTableData: TreeNode[] = [];

  // Método de formateo de moneda
  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  // Formateador para el grid
  private currencyFormatter = (params: ValueFormatterParams) => {
    if (params.value == null) return '';
    return this.formatCurrency(params.value);
  };

  // Chart configuration
  chartOptions: Highcharts.Options = {
    chart: {
      type: 'column'
    },
    title: {
      text: 'Gastos por Aprobar vs Presupuesto Proyectado',
      style: {
        fontSize: '14px'
      }
    },
    plotOptions: {
      column: {
        cursor: 'pointer',
        point: {
          events: {
            click: function(event: any) {
              const component = (this as any).series.chart.component;
              if (component) {
                component.filterGridByDepartment(this.category);
              }
            }
          }
        }
      }
    },
    xAxis: {
      categories: ['Tecnología', 'Auditoría', 'RR.HH', 'CND', 'Compras', 'Marketing', 'Finanzas'],
      title: {
        text: 'Departamento'
      }
    },
    yAxis: {
      title: {
        text: 'Valor'
      }
    },
    series: [{
      name: 'Gastos por Aprobar',
      type: 'column',
      data: [25000, 15000, 7000, 12000, 18000, 22000, 30000]
    }, {
      name: 'Presupuesto Proyectado',
      type: 'column',
      data: [30000, 20000, 10000, 15000, 20000, 25000, 35000]
    }]
  };

  // Pie chart configuration
  pieChartOptions: Highcharts.Options = {
    chart: {
      type: 'pie',
      marginBottom: 80
    },
    title: {
      text: 'Total Presupuesto vs Gastos por Aprobar',
      style: {
        fontSize: '14px'
      }
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      y: 0
    },
    tooltip: {
      pointFormatter: function() {
        return `${this.name}: ${new Intl.NumberFormat('es-EC', {
          style: 'currency',
          currency: 'USD'
        }).format(this.y as number)} (${Highcharts.numberFormat(this.percentage || 0, 1)}%)`;
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '{point.percentage:.1f}%',
          distance: -30,
          style: {
            fontSize: '16px',
            fontWeight: 'bold',
            textOutline: '2px contrast'
          }
        },
        showInLegend: true,
        size: '100%'
      }
    },
    series: [{
      name: 'Total',
      type: 'pie',
      data: [
        {
          name: 'Gastos por Aprobar',
          y: 129000,
          color: '#7cb5ec'
        },
        {
          name: 'Presupuesto Proyectado',
          y: 155000,
          color: '#434348'
        }
      ]
    }]
  };

  // Columnas para agrupación manual
  columnDefs: ColDef[] = [
    { field: 'departamento', rowGroup: true, hide: true },
    { field: 'centroCosto', rowGroup: true, hide: true },
    { field: 'ordenCompra', rowGroup: true, hide: true },
    { field: 'descripcion', headerName: 'Descripción', minWidth: 250, flex: 2 },
    { 
      field: 'valor', 
      headerName: 'Valor', 
      minWidth: 120, 
      flex: 1, 
      valueFormatter: this.currencyFormatter, 
      cellStyle: { 'text-align': 'right' } 
    },
    { 
      headerName: 'Aprobación',
      field: 'aprobadoNivel4',
      cellRenderer: 'approveButtonRenderer',
      cellRendererParams: { 
        onApprove: (data: FlatApprovalData) => this.approveItem(data, 4),
        showButton: (data: FlatApprovalData) => data.nivel === 4 && this.shouldShowApprovalButton(data, 4)
      },
      width: 120,
      pinned: 'right'
    }
  ];

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    sortable: true,
    resizable: true
  };

  frameworkComponents = {
    approveButtonRenderer: ApproveButtonRendererComponent
  };

  constructor(private currencyPipe: CurrencyPipe) {}

  ngOnInit(): void {
    this.allFlatData = this.generateSampleData();
    this.treeTableData = []; // TreeTable vacío al inicio
  }

  ngAfterViewInit(): void {
    try {
      this.chart = Highcharts.chart(this.chartContainer.nativeElement, this.chartOptions);
      (this.chart as any).component = this;
      this.pieChart = Highcharts.chart(this.pieChartContainer.nativeElement, this.pieChartOptions);
      // Asignar el handler de click a las barras
      if (this.chart && this.chart.series && this.chart.series[0]) {
        this.chart.series[0].update({
          point: {
            events: {
              click: (event: any) => {
                const department = event.point.category;
                this.filterTreeByDepartment(department);
              }
            }
          }
        });
      }
    } catch (e) {
      console.error('Error al inicializar los gráficos', e);
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    setTimeout(() => {
      this.gridApi.sizeColumnsToFit();
    }, 0);
  }

  filterGridByDepartment(department: string) {
    const flatData = this.generateSampleData().filter(item => item.departamento === department);
    this.rowData = flatData;
    if (this.gridApi) {
      this.gridApi.setRowData(flatData);
    }
  }

  shouldShowApprovalButton(data: FlatApprovalData, nivel: number): boolean {
    if (!data || data.isSubtotal) return false;
    
    switch(nivel) {
      case 1:
        return !data.aprobadoNivel1;
      case 2:
        return data.aprobadoNivel1 && !data.aprobadoNivel2;
      case 3:
        return data.aprobadoNivel2 && !data.aprobadoNivel3;
      case 4:
        return data.aprobadoNivel3 && !data.aprobadoNivel4;
      default:
        return false;
    }
  }

  approveItem(data: FlatApprovalData, nivel: number) {
    const nivelText = `Nivel ${nivel}`;
    Swal.fire({
      title: `¿Está seguro de aprobar este item en ${nivelText}?`,
      text: `${data.descripcion}\nValor: ${this.currencyPipe.transform(data.valor, 'USD', 'symbol', '1.0-0', 'es-EC')}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, aprobar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then(result => {
      if (result.isConfirmed) {
        switch(nivel) {
          case 1:
            data.aprobadoNivel1 = true;
            break;
          case 2:
            data.aprobadoNivel2 = true;
            break;
          case 3:
            data.aprobadoNivel3 = true;
            break;
          case 4:
            data.aprobadoNivel4 = true;
            break;
        }
        
        if (this.gridApi) {
          const rowNode = this.gridApi.getRowNode(data.ordenCompra);
          if (rowNode) {
            this.gridApi.refreshCells({
              rowNodes: [rowNode],
              force: true
            });
          }
        }
        
        Swal.fire('¡Aprobado!', `El item ha sido aprobado en ${nivelText} exitosamente.`, 'success');
      }
    });
  }

  // Genera datos planos con subtotales manuales
  generateSampleData(): FlatApprovalData[] {
    const data: FlatApprovalData[] = [
      // Departamento Tecnología
      { departamento: 'Tecnología', centroCosto: 'CC001', ordenCompra: 'SOLP-101', descripcion: 'Servidor Dell PowerEdge', valor: 6000, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC001', ordenCompra: 'SOLP-101', descripcion: 'Servidor HP ProLiant', valor: 4000, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC001', ordenCompra: 'SOLP-102', descripcion: 'Switch Cisco', valor: 3000, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC001', ordenCompra: 'SOLP-102', descripcion: 'Router Mikrotik', valor: 2000, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC002', ordenCompra: 'SOLP-201', descripcion: 'Microsoft Office 365', valor: 4000, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC002', ordenCompra: 'SOLP-201', descripcion: 'Adobe Creative Cloud', valor: 2000, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC002', ordenCompra: 'SOLP-202', descripcion: 'Visual Studio Enterprise', valor: 2500, nivel: 4 },
      { departamento: 'Tecnología', centroCosto: 'CC002', ordenCompra: 'SOLP-202', descripcion: 'JetBrains Suite', valor: 1500, nivel: 4 },
      // Departamento Auditoría
      { departamento: 'Auditoría', centroCosto: 'CC003', ordenCompra: 'SOLP-301', descripcion: 'Deloitte - Auditoría Financiera', valor: 5000, nivel: 4 },
      { departamento: 'Auditoría', centroCosto: 'CC003', ordenCompra: 'SOLP-301', descripcion: 'PWC - Auditoría de Procesos', valor: 4000, nivel: 4 },
      { departamento: 'Auditoría', centroCosto: 'CC004', ordenCompra: 'SOLP-401', descripcion: 'Curso ISO 27001', valor: 3500, nivel: 4 },
      { departamento: 'Auditoría', centroCosto: 'CC004', ordenCompra: 'SOLP-401', descripcion: 'Curso COBIT', valor: 2500, nivel: 4 },
      // Puedes agregar más departamentos y niveles aquí
    ];
    return data;
  }

  // TreeTable helpers
  approveTreeNode(node: TreeNode) {
    node.data.aprobado = true;
    // Si quieres guardar en backend, aquí iría la llamada
  }

  // buildTree ahora agrega el campo 'aprobado' en cada nodo
  buildTree(data: FlatApprovalData[]): TreeNode[] {
    const deptMap = new Map<string, TreeNode>();
    const ccMap = new Map<string, TreeNode>();
    const ocMap = new Map<string, TreeNode>();
    const tree: TreeNode[] = [];
    data.forEach(item => {
      // Departamento
      if (!deptMap.has(item.departamento)) {
        const deptNode: TreeNode = {
          data: { label: item.departamento, nivel: 1, valor: 0, aprobado: false },
          children: []
        };
        deptMap.set(item.departamento, deptNode);
        tree.push(deptNode);
      }
      // Centro de Costo
      const ccKey = item.departamento + '|' + item.centroCosto;
      if (!ccMap.has(ccKey)) {
        const ccNode: TreeNode = {
          data: { label: item.centroCosto, nivel: 2, valor: 0, aprobado: false },
          children: []
        };
        ccMap.set(ccKey, ccNode);
        deptMap.get(item.departamento)!.children!.push(ccNode);
      }
      // Orden de Compra
      const ocKey = ccKey + '|' + item.ordenCompra;
      if (!ocMap.has(ocKey)) {
        const ocNode: TreeNode = {
          data: { label: item.ordenCompra, nivel: 3, valor: 0, aprobado: false },
          children: []
        };
        ocMap.set(ocKey, ocNode);
        ccMap.get(ccKey)!.children!.push(ocNode);
      }
      // Detalle
      ocMap.get(ocKey)!.children!.push({
        data: {
          label: item.descripcion,
          nivel: 4,
          valor: item.valor,
          aprobado: false
        }
      });
    });
    // Sumarizar valores
    tree.forEach(deptNode => {
      deptNode.children?.forEach(ccNode => {
        ccNode.children?.forEach(ocNode => {
          ocNode.data.valor = ocNode.children?.reduce((sum, d) => sum + (d.data.valor || 0), 0) || 0;
        });
        ccNode.data.valor = ccNode.children?.reduce((sum, o) => sum + (o.data.valor || 0), 0) || 0;
      });
      deptNode.data.valor = deptNode.children?.reduce((sum, c) => sum + (c.data.valor || 0), 0) || 0;
    });
    return tree;
  }

  filterTreeByDepartment(department: string) {
    const filtered = this.allFlatData.filter(item => item.departamento === department);
    this.treeTableData = this.buildTree(filtered);
  }
} 